// One PvP fight for every mode (season one; design note mythic-idle-design/board.md, "Wired in"): the Arena and ranked,
// Element Halls, friendly duels, training, practice runs, tournaments and Trail trainers all fight through pvpBattle.
// Never Guild War (simulateWar), hunts or bosses.
//
// - The admin switch (kv 'board-on', on by default) picks the engine. Off: shared/arena.ts battle(), fronts in order,
//   exactly as before the board (same seed, same result). On: the two-row board (shared/board.ts boardBattle).
// - Each side is built the same way in every mode (sideFighters): one of each species, pvpFighter (fight level, PvP
//   stats, trait, relic at its cube root, Rune Tablet and held item), then synergies (synergyMods + withSynergy, a ranked season's
//   featured region included), then rows. On the board, held items and synergies take the board rules' scales
//   (shared/board.ts BoardRules.heldScale and synergyScale; board.md "Board scaling"); fronts in order never do.
// - On board rules 3 and up each creature then fights at its fight level × (1 ± up to BoardRules.swing), drawn from the
//   fight's seed before the first blow: side a's creatures in order, then side b's (boardTeam). A replay on the same
//   seed and rules draws the same.
// - On board rules 4 and up an element trait counts for the creature's own element (BoardRules.ownTraits,
//   shared/traits.ts ownTrait); before that, as stored. Fronts in order (the switch off) keep the trait as stored.
// - Rows come with the line-up, front or back per creature (at most BOARD.frontMax front). A line-up saved without rows,
//   and every NPC without set rows (Hall Keepers' old registrations, most Trail trainers), stands on defaultRows:
//   tanks in front, carries behind.
// - The result is a BattleResult either way; a board result carries `board` (the board rules version), each creature's
//   row and reach, and both creatures' charge on every blow, for the replay.
import { battle, synergyPer, type ArenaCreature, type BattleOpts, type BattleResult } from './arena.ts';
import { BOARD, boardBattle, boardRules, defaultRows, type BoardRow, type BoardRules, type BoardTeam } from './board.ts';
import type { Fighter } from './combat.ts';
import { HELD_SWITCH, type HeldScale } from './held.ts';
import { fightLevel, oneOfEach, pvpFighter } from './pvp.ts';
import { synergyMods, withSynergy, type SynergyOpts, type SynergyScale } from './synergy.ts';
import { speciesTrait } from './traits.ts';
import type { Rng } from './rng.ts';

/**
 * Live switch, mirrored from the server (kv 'board-on'): the server sets it at start and when an admin flips it, the
 * client from 'hello' and the 'board' message. pvpBattle reads it when no explicit choice is passed.
 */
export const BOARD_SWITCH = { on: true };

/** A side as a mode hands it in: its line-up (in slot order) and, optionally, a row per creature (same order). */
export interface PvpSide { name: string; team: ArenaCreature[]; rows?: readonly (BoardRow | null | undefined)[] | null }

/**
 * BattleOpts plus board: true / false, absent = the live switch; boardV: the board rules version (shared/board.ts
 * BOARD_RULES; a tournament game's own), absent = the live one (BOARD).
 */
export interface PvpOpts extends BattleOpts { board?: boolean; boardV?: number }

export const isRow = (x: unknown): x is BoardRow => x === 'front' || x === 'back';
export const frontCount = (rows: readonly (BoardRow | null | undefined)[]) => rows.filter((r) => r === 'front').length;

/**
 * Rows sent with a line-up (a message or a save) for `n` creatures. Absent (undefined or null) is fine: the line-up
 * stands on defaultRows. Otherwise one 'front' or 'back' per creature, at most BOARD.frontMax front.
 */
export function cleanRows(rows: unknown, n: number): { ok: true; rows: BoardRow[] | null } | { ok: false; why: string } {
  if (rows === undefined || rows === null) return { ok: true, rows: null };
  if (!Array.isArray(rows) || rows.length !== n || !rows.every(isRow)) return { ok: false, why: 'Set each creature to the front or the back row.' };
  if (frontCount(rows) > BOARD.frontMax) return { ok: false, why: `At most ${BOARD.frontMax} creatures in the front row.` };
  return { ok: true, rows: [...rows] };
}

/** Rows for `ids`, read by creature id from a saved line-up (ids with rows in the same order). Undefined: no rows saved. */
export function rowsFor(ids: readonly number[], saved: { ids: readonly number[]; rows?: readonly BoardRow[] | null } | null | undefined): (BoardRow | undefined)[] | undefined {
  if (!saved?.rows) return undefined;
  return ids.map((id) => { const i = saved.ids.indexOf(id); return i >= 0 ? saved.rows![i] : undefined; });
}

export interface SideFighters {
  /** The creatures that fight (one of each species), and each one's index in the team handed in. */
  kept: ArenaCreature[];
  idx: number[];
  fighters: Fighter[];
}

/** The scales a board rules version puts on synergies and held items; absent when it has none (version 1). */
const scaleOf = <T extends object>(x: T): T | undefined => (Object.keys(x).length ? x : undefined);

/**
 * A side's fighters on the board, as every PvP mode builds them: one of each species, pvpFighter (held item
 * included), its trait as the rules read it (ownTraits), synergies, each under the board rules' scales and relic power
 * (`rules`: the live board unless a replay names its own).
 */
export function sideFighters(team: readonly ArenaCreature[], syn: SynergyOpts | null, held = HELD_SWITCH.on, rules: Pick<BoardRules, 'synergyScale' | 'heldScale' | 'ownTraits' | 'relicExp'> = BOARD, swing?: { by: number; rng: Rng }): SideFighters {
  const tagged = oneOfEach(team.map((c, i) => ({ c, i, species: c.species })));
  const kept = tagged.map((x) => x.c);
  const sScale = scaleOf(rules.synergyScale), hScale = scaleOf(rules.heldScale);
  const mods = syn ? synergyMods(kept, sScale ? { ...syn, scale: sScale } : syn) : null;
  const at = (c: ArenaCreature) => (swing ? Math.max(1, Math.round(fightLevel(c.level) * (1 + swing.by * (2 * swing.rng.next() - 1)))) : undefined);
  const fighters = kept.map((c, i) => {
    let f = pvpFighter(c, 'arena', held, hScale, at(c), rules.relicExp);
    if (rules.ownTraits) f = { ...f, trait: speciesTrait(f.trait, f.speciesId) };
    return mods ? withSynergy(f, mods[i]) : f;
  });
  return { kept, idx: tagged.map((x) => x.i), fighters };
}

/**
 * Rows for the creatures that fight: the side's own rows when it set any (a creature it left unset goes to the back),
 * else defaultRows on the fighters as they fight (synergies and held items included).
 */
export function sideRows(sf: SideFighters, rows: PvpSide['rows']): BoardRow[] {
  const mine = sf.idx.map((i) => rows?.[i]);
  if (mine.some(isRow)) return mine.map((r) => (isRow(r) ? r : 'back'));
  return defaultRows(sf.fighters);
}

/** The rows a line-up would stand on, for line-up screens: its own rows, else defaultRows (synergies and held items on as live). */
export function rowsShown(team: readonly ArenaCreature[], rows: PvpSide['rows'], syn: SynergyOpts | null): BoardRow[] {
  const sf = sideFighters(team, syn);
  const out: BoardRow[] = team.map(() => 'back');
  sideRows(sf, rows).forEach((r, k) => { out[sf.idx[k]] = r; });
  return out;
}

/**
 * A side as the board takes it (`rules`: the board rules version it fights on, the live one by default). With `rng`
 * and a rules version that has a swing, each creature's fight level swings (drawn from `rng`); rows set by default
 * stand on the unswung fighters, as line-up screens show them.
 */
export function boardTeam(side: PvpSide, syn: SynergyOpts | null, held = HELD_SWITCH.on, rules: Pick<BoardRules, 'synergyScale' | 'heldScale' | 'swing' | 'ownTraits' | 'relicExp'> = BOARD, rng?: Rng): BoardTeam {
  const sf = sideFighters(side.team, syn, held, rules);
  const rows = sideRows(sf, side.rows);
  const fighters = rng && rules.swing ? sideFighters(side.team, syn, held, rules, { by: rules.swing, rng }).fighters : sf.fighters;
  return { name: side.name, team: sf.kept.map((c, k) => ({ f: fighters[k], row: rows[k], id: c.id, level: c.level, aura: c.rolls.aura })) };
}

/**
 * A PvP fight. Side 1 is the defender. Board off (opts.board, else the live switch): battle() exactly as before, rows
 * ignored. Board on: boardBattle on both sides' rows, on board rules opts.boardV (absent: the live ones), whose scales
 * apply to synergies and held items, whose relicExp to relics (opts.relicExp is for fronts in order) and whose swing
 * moves each creature's fight level for this fight. Synergies and held items follow opts as battle() reads them. The result keeps
 * each side's synergy options unscaled; its `board` names the version, and so the scale (synergyScaleOf).
 */
export function pvpBattle(a: PvpSide, b: PvpSide, rng: Rng, opts: PvpOpts = {}): BattleResult {
  const { board = BOARD_SWITCH.on, boardV, ...rest } = opts;
  if (!board) return battle({ name: a.name, team: a.team }, { name: b.name, team: b.team }, rng, 'arena', rest);
  const per = synergyPer(rest);
  const held = rest.held ?? HELD_SWITCH.on;
  const R = boardRules(boardV);
  const A = boardTeam(a, per[0], held, R, rng), B = boardTeam(b, per[1], held, R, rng);
  const r = boardBattle(A, B, rng, R);
  return { ...r, ...(per[0] || per[1] ? { synergy: per } : {}) };
}

/**
 * The synergy scale of a fight: a result's board rules version (BattleResult.board), or the live fight (`'live'`: the
 * board's while its switch is on). Undefined: none (fronts in order, board rules 1).
 */
export function synergyScaleOf(board: number | undefined | 'live'): SynergyScale | undefined {
  const v = board === 'live' ? (BOARD_SWITCH.on ? BOARD.version : undefined) : board;
  return v ? scaleOf(boardRules(v).synergyScale) : undefined;
}
/** The held-item scale of a fight, as synergyScaleOf. */
export function heldScaleOf(board: number | undefined | 'live'): HeldScale | undefined {
  const v = board === 'live' ? (BOARD_SWITCH.on ? BOARD.version : undefined) : board;
  return v ? scaleOf(boardRules(v).heldScale) : undefined;
}

/** Whether a result was fought on the board. */
export const onBoard = (r: Pick<BattleResult, 'board'>) => !!r.board;
